import gsap from 'gsap';

import { scrollTo } from '../common/utils';
import pageContent from '../components/page-content';

const els = {
    $items: ($accordion) => $accordion.find('[data-accordion-item]'),
    $button: ($item) => $item.find('[data-accordion-item-button]'),
    $icon: ($item) => $item.find('[data-accordion-item-button-icon]'),
    $content: ($item) => $item.find('[data-accordion-item-content]'),
    $iconLine: ($icon) => $icon.find('[data-accordion-item-button-icon-line]'),
    $accordionForItem: ($item) => $item.parents('[data-accordion]'),
    $activeItem: ($accordion) => els.$items($accordion).filter('[data-open=1]').first(),
};

const isAccordion = ($target) => (
    $target.is('[data-accordion]')
);

const isItem = ($target) => (
    $target.is('[data-accordion-item]')
);

const isItemOpen = ($item) => (
    $item.attr('data-open') === '1'
);

const scrollToItem = async ($item) => {
    scrollTo(
        $item,
        { scroller: pageContent.els.$content() }
    );
};

const usesScrollBehavior = ($accordion) => (
    $accordion.is('[data-use-scroll-behavior]')
);

const openItem = async ($item) => {
    const $icon = els.$icon($item);
    const $content = els.$content($item);
    const $accordion = els.$accordionForItem($item);
    const shouldUseScrollBehavior = usesScrollBehavior($accordion);

    if ($item.attr('data-open') === '1') {
        return;
    }

    $item.attr('data-open', '1');

    const timeline = gsap.timeline({
        paused: true,
    });

    timeline.set($content, { clearProps: 'display' });
    timeline.set($content, { height: 'auto' });
    timeline.from($content, { height: 0 });
    timeline.add(makeIconOpenTween($icon), '<');
    timeline.set($content, { height: 'auto' });

    $accordion.trigger('accordion.item.open', [$item]);

    await Promise.all([
        closeItemsExcept($item),
        timeline.play(),
    ]);

    if (shouldUseScrollBehavior) {
        await scrollToItem($item);
    }
};

const closeItem = async ($item) => {
    const $icon = els.$icon($item);
    const $content = els.$content($item);
    const $accordion = els.$accordionForItem($item);

    if ($item.attr('data-open') !== '1') {
        return;
    }

    $item.attr('data-open', '0');

    const timeline = gsap.timeline({
        paused: true,
    });

    timeline.to($content, { height: 0 });
    timeline.add(makeIconCloseTween($icon), '<');
    timeline.set($content, { display: 'none' });
    timeline.set($content, { clearProps: 'height' });

    $accordion.trigger('accordion.item.close', [$item]);

    await timeline.play();
};

const toggleItem = async ($item) => {
    const isOpen = isItemOpen($item);

    if (isOpen) {
        await closeItem($item);
    } else {
        await openItem($item);
    }
};

const closeItemsExcept = async ($item) => {
    const $accordion = els.$accordionForItem($item);
    const $otherItems = els.$items($accordion).not($item);

    await Promise.all(
        $otherItems.map(
            (_, otherItem) => closeItem($(otherItem))
        )
    );
};

const makeIconOpenTween = ($icon, { duration = 0.5 } = {}) => {
    if ($icon.is('[data-type=angle]')) {
        return (
            gsap.to(
                $icon,
                {
                    rotate: 90,
                    duration,
                }
            )
        );
    } else if ($icon.is('[data-type=plus]')) {
        return (
            gsap.to(
                els.$iconLine($icon).eq(1),
                {
                    rotate: 90,
                    duration,
                }
            )
        );
    }
};

const makeIconCloseTween = ($icon, { duration = 0.5 } = {}) => {
    if ($icon.is('[data-type=angle]')) {
        return (
            gsap.to(
                $icon,
                {
                    rotate: 0,
                    duration,
                }
            )
        );
    } else if ($icon.is('[data-type=plus]')) {
        return (
            gsap.to(
                els.$iconLine($icon).eq(1),
                {
                    rotate: 0,
                    duration,
                }
            )
        );
    }
};

const initializeIconAsOpen = () => {
    const tween = makeIconOpenTween($icon, { duration: 0 });
    tween.play();
};

const initializeAccordion = (accordion) => {
    const $accordion = $(accordion);
    const $items = els.$items($accordion);

    $items.each((_, item) => {
        const $item = $(item);
        const $button = els.$button($item);
        const $icon = els.$icon($item);
        const $content = els.$content($item);

        if (!$item.is('[data-initially-open]')) {
            $item.attr('data-open', '0');
            gsap.set($content, { display: 'none' });
        } else {
            $item.attr('data-open', '1');
            gsap.set($content, { clearProps: 'display' });
            gsap.set($content, { height: 'auto' });
            initializeIconAsOpen($icon);
        }

        gsap.set($content, { overflow: 'hidden' });

        $button.on('click', (event) => {
            event.preventDefault();
            toggleItem($item);
        });
    });
};

const initialize = () => {
    $('[data-accordion]').each((_, element) => {
        initializeAccordion(element);
    });
};

export default {
    initialize,
    els,
    isAccordion,
    isItem,
    isItemOpen,
    openItem,
    closeItem,
    toggleItem,
    closeItemsExcept,
    usesScrollBehavior,
};

